import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
  useToast,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";



import ViewCOCModal from "../../components/all-cocs-screen/ViewCOCModal";

import {
  CInput,
  CSelect,
  DeleteCOCModal,
  DetailedAnalysisOrderModal,
  DetailedAnalysisReportModal,
  SubmitToExternalIntegrationModal,
  UpdateStatusModal,
} from "../../components";
import {
  AuthModel,
  CocDeleteRequestModel,
  CocModel,
  FileModel,
} from "../../services/models";
import { FaRegCircleQuestion, FaRegFilePdf } from "react-icons/fa6";
import { exportExcel } from "../../utils/exportExcel";
import { ASSET_URL } from "../../services/axios";
import {
  formatDate,
  formatSamplingFor,
  getRelativeTimeFromNow,
} from "../../utils";
import { useOrder } from "../../context/OrderContext";
import { COC_STATUS } from "../../constants";

export default function AllCOCs() {
  const user = AuthModel.getUser();
  const { DecrementReportUnSeenCount } = useOrder();
  const [pending, setPending] = useState(true);
  const [cocs, setCocs] = useState([]);
  const [filteredCocs, setFilteredCocs] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [searchLocation, setSearchLocation] = useState("");
  const [debouncedLocation, setDebouncedLocation] = useState("");
  const [searchStatus, setSearchStatus] = useState(null);
  const toast = useToast();
  const statusOptions = [
    { label: "Pending", value: "pending" },
    { label: "Received", value: "received" },
    { label: "Analysed", value: "analysed" },
    { label: "DA Requested", value: "da-requested" },
    { label: "DA Completed", value: "da-completed" },
    { label: "Pending Deletion", value: "pending-deletion" },
    { label: "Draft", value: "draft" },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "gray";
      case "pending":
        return "red";
      case "received":
        return "blue";
      case "analysed":
        return "green";
      case "pending-deletion":
        return "yellow";
      case "da-requested":
        return "red";
      case "da-completed":
        return "green";
      default:
        return "red";
    }
  };

  const getStatusLabel = (status) => {
    if (status === statusOptions[3].value) return statusOptions[3].label;
    if (status === statusOptions[4].value) return statusOptions[4].label;
    if (status === statusOptions[5].value) return statusOptions[5].label;
    return status;
  };

  const getLocationText = (row) =>
    row.propertyType !== "vehicle"
      ? `${row.address}, ${row.city}, ${row.state}, ${row.postcode}`
      : `Vehicle ${row.VIN}, ${row.rego}, ${row.regoState}`;

  const samplingTypeMeta = {
    "laboratory-composite-order": {
      code: "LC",
      label: "Laboratory Composite",
    },
    "detailed-analysis-order": {
      code: "DA",
      label: "Detailed Analysis",
    },
    "validation-analysis-order": {
      code: "VL",
      label: "Validation",
    },
  };

  const getSamplingTypeMeta = (value) =>
    samplingTypeMeta[value] || { code: "NR", label: "Report" };

  const hasResultPdf = (row) =>
    Boolean(
      row.orderFiles?.some((f) => f.fileName === "pdfFile" && f.fileURL)
    );

  const hasRefId = (row) => {
    const r = row?.refId;
    return r != null && String(r).trim() !== "";
  };

  const customStyles = {
    headRow: {
      style: {
        borderBottomWidth: "0px",
      },
    },
    headCells: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
    rows: {
      style: {
        borderRadius: "15px",
        boxShadow: "1px 2px 6px 0px rgb(0,0,0,0.1)",
        marginBottom: "10px",
        "&:not(:last-of-type)": {
          borderBottomWidth: "0px",
        },
      },
    },
    pagination: {
      style: {
        borderTopWidth: "0px",
      },
    },
  };
  const renderReportControl = (item) => {
    const hasReport =
      (item.status !== statusOptions[0] || item.status !== statusOptions[1]) &&
      item.orderFiles.length &&
      item.orderFiles.find((obj) => obj.fileName === "pdfFile");

    if (!hasReport) return null;

    const pdfFile = item.orderFiles.find((obj) => obj.fileName === "pdfFile");

    if (user.role === "admin") {
      return (
        <Tooltip label="Download report">
          <IconButton
            onClick={() => downloadPDF(ASSET_URL + pdfFile?.fileURL)}
            bgColor="primary.100"
            color="red.400"
            fontSize="xl"
            icon={<FaRegFilePdf />}
            isRound
          />
        </Tooltip>
      );
    }

    const { code, label } = getSamplingTypeMeta(item.samplingFor);

    return (
      <Tooltip label={`${label} Report`}>
        <Button
          onClick={() => {
            downloadPDF(ASSET_URL + pdfFile?.fileURL);
            onMarkAsSeen(item._id);
          }}
          bgColor="transparent"
          color="primary.main"
          borderColor="primary.main"
          borderWidth="2px"
          fontWeight="bold"
          fontSize="xs"
          h="32px"
          w="32px"
          minW="32px"
          rounded="full"
          p={0}
        >
          {code}
        </Button>
      </Tooltip>
    );
  };

  const isSeenColumn = {
    name: "",
    cell: (row) => {
      let isUnseen = false;

      if(user.role === "admin") {
        const isUnseenByAdmin = row.seenByAdmin !== undefined && row.seenByAdmin === false;

        if([COC_STATUS.PENDING, COC_STATUS.RECEIVED].includes(row.status) && isUnseenByAdmin) {
          isUnseen = isUnseenByAdmin;
        }
      } else {
        const isUnseenByCustomer = row.seenByCustomer !== undefined && row.seenByCustomer === false;

        if([COC_STATUS.ANALYSED, COC_STATUS.DA_COMPLETED].includes(row.status) && isUnseenByCustomer) {
          isUnseen = isUnseenByCustomer;
        }
      }
      
      if (isUnseen) {
        return (
          <Box
            bgColor="red.500"
            borderRadius="50%"
            margin="0"
            w="12px"
            h="12px"
          />
        );
      }
      return null;
    },
    width: "30px",
    minWidth: "30px",
  };

  const actionColumn = {
    name: "Action",
    cell: (row) => {
      return (
        <HStack>
          <ViewCOCModal
            data={row}
            userRole={user.role}
            prepareOrderExcelData={prepareOrderExcelData}
            prepareSampleExcelData={prepareSampleExcelData}
            refetchData={fetchCocs}
            onUpdateStatus={onUpdateStatus}
            onMarkAsSeen={onMarkAsSeen}
          />
          {user.role === "user" &&
            hasResultPdf(row) &&
            hasRefId(row) && (
            <SubmitToExternalIntegrationModal
              data={row}
              refetchData={fetchCocs}
              userRole={user.role}
            />
          )}
          {row.status !== "pending-deletion" && (
            <>
              {renderReportControl(row)}
              {(row.status === statusOptions[0].value ||
                row.status === statusOptions[1].value) &&
                user.role === "admin" && (
                  <UpdateStatusModal
                    data={row}
                    onUpdateStatus={onUpdateStatus}
                  />
                )}
              {row.status === statusOptions[0].value &&
                user.role !== "admin" && (
                  <DeleteCOCModal data={row} onDelete={onDelete} />
                )}
              {row.status === "analysed" &&
              row.samplingFor === "laboratory-composite-order" &&
              row.orderFiles.length &&
              !row.orderFiles.find((obj) => obj.fileName === "DAFile") &&
              user.role !== "admin" ? (
                <Tooltip
                  label={
                    row.status === statusOptions[3].value
                      ? "Requested"
                      : "Request DA Order"
                  }
                >
                  <Button
                    onClick={() => onUpdateStatus(row, statusOptions[3].value)}
                    bgColor="primary.main"
                    color="primary.100"
                    size="xs"
                    leftIcon={<FaRegCircleQuestion />}
                    isDisabled={row.status === statusOptions[3].value}
                    rounded="full"
                  >
                    Request DA
                  </Button>
                </Tooltip>
              ) : null}
              {row.status === statusOptions[3].value &&
              row.samplingFor === "laboratory-composite-order" &&
              user.role === "admin" ? (
                <DetailedAnalysisOrderModal
                  data={row}
                  statusOptions={statusOptions}
                  onUpdateStatus={onUpdateStatus}
                />
              ) : null}
              {row.status === statusOptions[4].value &&
              row.samplingFor === "laboratory-composite-order" ? (
                <DetailedAnalysisReportModal
                  data={row.orderFiles.find((obj) => obj.fileName === "DAFile")}
                />
              ) : null}
            </>
          )}
        </HStack>
      );
    },
    hide: "md",
  };

  const adminColumns = [
    isSeenColumn,
    {
      name: "Company Name",
      selector: (row) => row.clientName,
    },
    {
      name: "Report Status",
      cell: (row) => (
        <Badge
          colorScheme={getStatusColor(row.status)}
          rounded="full"
          textTransform="capitalize"
          px="1.5"
          pb="0.5"
        >
          {getStatusLabel(row.status)}
        </Badge>
      ),
    },
    {
      name: "Client Email",
      selector: (row) => row.email,
      hide: "md",
    },
    {
      name: "Testing Location",
      selector: (row) => getLocationText(row),
      hide: "md",
    },
    {
      name: "Date Sampled",
      selector: (row) => formatDate(row.dateSampled),
      hide: "md",
    },
    actionColumn,
  ];

  const userColumns = [
    isSeenColumn,
    {
      name: "Location",
      selector: (row) => getLocationText(row),
    },
    {
      name: "Date Sampled",
      selector: (row) => formatDate(row.dateSampled),
    },
    {
      name: "Status",
      cell: (row) => {
        const relativeTime =
          getRelativeTimeFromNow(
            row.statusUpdatedAt ||
              row.updatedAt ||
              row.dateAnalysed ||
              row.dateReceived || row.dateCreated
          ) || "";
        return (
          <VStack alignItems="flex-start" spacing={0}>
            <Badge
              colorScheme={getStatusColor(row.status)}
              rounded="full"
              textTransform="capitalize"
              px="1.5"
              pb="0.5"
            >
              {getStatusLabel(row.status)}
            </Badge>
            {relativeTime ? (
              <Text fontSize="xs" color="gray.500">
                Updated {relativeTime}
              </Text>
            ) : null}
          </VStack>
        );
      },
    },
    {
      name: "Test Type",
      selector: (row) => formatSamplingFor(row.samplingFor),
    },
    actionColumn,
  ];

  const columns = user.role === "admin" ? adminColumns : userColumns;

  const expandableData = ({ data }) => {
    return (
      <VStack alignItems="start" p={3}>
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          gap={{ base: 2, md: 4 }}
        >
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              Client Email
            </Text>
            <Text fontSize="sm">{data.email}</Text>
          </Box>
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              Testing Location
            </Text>
            <Text fontSize="sm">{getLocationText(data)}</Text>
          </Box>
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              Date Sampled
            </Text>
            <Text fontSize="sm">{formatDate(data.dateSampled)}</Text>
          </Box>
        </Flex>
        <Divider my={1} />
        <HStack alignItems="center" justifyContent="end" w="full">
          <ViewCOCModal
            data={data}
            userRole={user.role}
            prepareOrderExcelData={prepareOrderExcelData}
            prepareSampleExcelData={prepareSampleExcelData}
            refetchData={fetchCocs}
            onUpdateStatus={onUpdateStatus}
            onMarkAsSeen={onMarkAsSeen}
          />
          {user.role === "user" &&
            hasResultPdf(data) &&
            hasRefId(data) && (
            <SubmitToExternalIntegrationModal
              data={data}
              refetchData={fetchCocs}
              userRole={user.role}
            />
          )}
          {data.status !== "pending-deletion" && (
            <>
              {renderReportControl(data)}
              {(data.status === statusOptions[0].value ||
                data.status === statusOptions[1].value) &&
                user.role === "admin" && (
                  <UpdateStatusModal
                    data={data}
                    onUpdateStatus={onUpdateStatus}
                  />
                )}
              {data.status === statusOptions[0].value &&
                user.role !== "admin" && (
                  <DeleteCOCModal data={data} onDelete={onDelete} />
                )}
              {/* {row.status === "analysed" &&
          user.role === "admin" &&
          row.reportType === "manual" && (
            <Tooltip label="Copy report link">
              <IconButton
                onClick={() => {
                  navigator.clipboard.writeText(
                    window.location.host + `/report/${row._id}`
                  );
                  toast({
                    description: "Link Copied",
                    status: "success",
                    isClosable: true,
                  });
                }}
                bgColor="primary.100"
                color="primary.main"
                fontSize="xl"
                icon={<BiLink />}
                isRound
              />
            </Tooltip>
          )} */}
              {/* {(row.status === "received" || row.status === "analysed") &&
          user.role === "admin" && (
            <ExportToExcelModal
              data={row}
              prepareOrderExcelData={prepareOrderExcelData}
              prepareSampleExcelData={prepareSampleExcelData}
            />
          )} */}
              {data.status === "analysed" &&
              data.samplingFor === "laboratory-composite-order" &&
              data.orderFiles.length &&
              !data.orderFiles.find((obj) => obj.fileName === "DAFile") &&
              user.role !== "admin" ? (
                <Tooltip
                  label={
                    data.status === statusOptions[3].value
                      ? "Requested"
                      : "Request DA Order"
                  }
                >
                  <Button
                    onClick={() => onUpdateStatus(data, statusOptions[3].value)}
                    bgColor="primary.main"
                    color="primary.100"
                    size="xs"
                    leftIcon={<FaRegCircleQuestion />}
                    isDisabled={data.status === statusOptions[3].value}
                    rounded="full"
                  >
                    Request DA
                  </Button>
                </Tooltip>
              ) : null}
              {data.status === statusOptions[3].value &&
              data.samplingFor === "laboratory-composite-order" &&
              user.role === "admin" ? (
                <DetailedAnalysisOrderModal
                  data={data}
                  statusOptions={statusOptions}
                  onUpdateStatus={onUpdateStatus}
                />
              ) : null}
              {data.status === statusOptions[4].value &&
              data.samplingFor === "laboratory-composite-order" ? (
                <DetailedAnalysisReportModal
                  data={data.orderFiles.find(
                    (obj) => obj.fileName === "DAFile"
                  )}
                />
              ) : null}
            </>
          )}
        </HStack>
      </VStack>
    );
  };

  const [windowSize, setWindowSize] = useState(0);

  // Window Resize Listener
  useLayoutEffect(() => {
    const updateWindowSize = () => setWindowSize(window.innerWidth);
    window.addEventListener("resize", updateWindowSize);
    updateWindowSize();

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  const onMarkAsSeen = async (cocId) => {
    try {
      let res = await CocModel.markAsSeen(cocId);
      if (res.status === 200) {
        let newData = [...cocs];
        let foundIndex = cocs.findIndex((item) => item._id === cocId);
        if (user.role === "admin") {
          newData[foundIndex].seenByAdmin = true;
        } else {
          newData[foundIndex].seenByCustomer = true;
        }
        setCocs(newData);
        newData = [...filteredCocs];
        foundIndex = filteredCocs.findIndex((item) => item._id === cocId);
        if (user.role === "admin") {
          newData[foundIndex].seenByAdmin = true;
        } else {
          newData[foundIndex].seenByCustomer = true;
        }
        setFilteredCocs(newData);
        //update context
        DecrementReportUnSeenCount();
      }
    } catch (error) {
      toast({
        description: "Something went wrong",
        status: "error",
        isClosable: true,
      });
      console.log(error);
    }
  };

  const onUpdateStatus = async (data, value) => {
    try {
      let todaysDate = new Date();
      let newData = [...cocs];
      let foundIndex = cocs.findIndex((item) => item._id === data._id);
      newData[foundIndex].status = value;
      if (value === statusOptions[0].value) {
        newData[foundIndex].dateReceived = "";
      }
      if (value === statusOptions[1].value) {
        newData[foundIndex].dateReceived = todaysDate;
        newData[foundIndex].questions = [...data.questions];
      }
      if (value === statusOptions[2].value) {
        newData[foundIndex].dateAnalysed = todaysDate;
        //upload files
        for (let i = 0; i < data.orderFiles.length; i++) {
          if (data.orderFiles[i].fileURL instanceof File) {
            let response = await FileModel.upload({
              file: data.orderFiles[i].fileURL,
            });
            if (response.status === 200) {
              newData[foundIndex].orderFiles.push({
                ...data.orderFiles[i],
                fileURL: response.data.fileURL,
              });
            } else {
              throw new Error("File upload failed!");
            }
          }
        }
      }
      if (value === statusOptions[4].value) {
        let dafile = data.orderFiles.find((obj) => obj.fileName === "DAFile");
        if (dafile && dafile?.fileURL instanceof File) {
          let response = await FileModel.upload({
            file: dafile.fileURL,
          });
          if (response.status === 200) {
            newData[foundIndex].orderFiles.push({
              fileName: dafile.fileName,
              fileURL: response.data.fileURL,
            });
          } else {
            throw new Error("File upload failed!");
          }
        }
      }

      let response = await CocModel.update(data._id, newData[foundIndex]);
      if (response.status === 200) {
        newData = [...cocs];
        foundIndex = cocs.findIndex((item) => item._id === data._id);
        newData[foundIndex] = response.data.data;
        setCocs(newData);
        newData = [...filteredCocs];
        foundIndex = filteredCocs.findIndex((item) => item._id === data._id);
        newData[foundIndex] = response.data.data;
        setFilteredCocs(newData);
        toast({
          description: "Status update successfully",
          status: "success",
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        description: "Something went wrong",
        status: "error",
        isClosable: true,
      });
      console.log(error);
    }
  };

  const onDelete = async (values) => {
    try {
      let response = await CocDeleteRequestModel.create(values);
      let coc = cocs.find((item) => item._id === values.coc);
      if (!coc.dateReceived) {
        coc.dateReceived = "";
      }
      response = await CocModel.update(coc._id, {
        ...coc,
        status: "pending-deletion",
      });
      if (response.status === 200) {
        let newData = [...cocs];
        let foundIndex = cocs.findIndex((item) => item._id === values.coc);
        newData[foundIndex].status = "pending-deletion";
        setCocs(newData);
        newData = [...filteredCocs];
        foundIndex = filteredCocs.findIndex((item) => item._id === values.coc);
        newData[foundIndex].status = "pending-deletion";
        setFilteredCocs(newData);
      }
      toast({
        description: "Deletion Requested successfully",
        status: "success",
        isClosable: true,
      });
    } catch (error) {
      toast({
        description: "Something went wrong",
        status: "error",
        isClosable: true,
      });
    }
  };

  const prepareOrderExcelData = (data) => {
    let comments = data.questions
      .filter((item, i) => (i === 4 && item.answer) || item.answer === false)
      .map((item) => item.comment)
      .toString();

    let newData = {
      "Client Name": data.clientName,
      "Contact Name": data.contactName,
      Phone: data.phone.replace(/\s/g, ""),
      "E-mail": data.email,
      "Sampling For": data.samplingFor,
      "Date Sampled": data.dateSampled,
      "Sampling Technique": data.samplingTechnique,
      "Security Seal No": data.securitySealNo,
      "Property Type": data.propertyType,
    };
    if (data.propertyType !== "vehicle") {
      newData["Address"] = data.address;
      newData["City"] = data.city;
      newData["State"] = data.state;
      newData["Postcode"] = data.postcode;
    } else {
      newData["Registration Number"] = data.rego;
      newData["State of Registration"] = data.regoState;
      newData["VIN Number"] = data.VIN;
    }
    newData["Comments"] = comments;
    newData["Order Type (rdtp_name)"] = data.samplingFor.split("-").join(" ");
    newData["Status"] = "Pending";
    let arrayData = [];
    arrayData.push(newData);
    let reportName =
      data.propertyType !== "vehicle"
        ? data.address +
          ", " +
          data.city +
          ", " +
          data.state +
          ", " +
          data.postcode
        : data.VIN;
    exportExcel(arrayData, reportName + "-report");
  };

  const prepareSampleExcelData = (data) => {
    let newData = [];

    for (let i = 0; i < data.samples.length; i++) {
      if ((i + 1) % 20 === 1) {
        if (i === 0) {
          let obj = {};
          if (data.propertyType !== "vehicle") {
            obj["Address"] = data.address;
          } else {
            obj["VIN Number"] = data.VIN;
          }
          obj["Sample Location"] = "Field Blank";
          obj["Sample Number"] = "Field Blank";
          obj["Status (stts_name)"] = "Pending";
          newData.push(obj);
        } else {
          let obj = {};
          if (data.propertyType !== "vehicle") {
            obj["Address"] = data.address;
          } else {
            obj["VIN Number"] = data.VIN;
          }
          obj["Sample Location"] = data.samples[i].value;
          obj["Sample Number"] = i + 1;
          obj["Status (stts_name)"] = "Pending";
          newData.push(obj);
        }
        let obj = {};
        if (data.propertyType !== "vehicle") {
          obj["Address"] = data.address;
        } else {
          obj["VIN Number"] = data.VIN;
        }
        obj["Sample Location"] = data.samples[i].value;
        obj["Sample Number"] = i + 1;
        obj["Status (stts_name)"] = "Pending";
        newData.push(obj);
      } else {
        let obj = {};
        if (data.propertyType !== "vehicle") {
          obj["Address"] = data.address;
        } else {
          obj["VIN Number"] = data.VIN;
        }
        obj["Sample Location"] = data.samples[i].value;
        obj["Sample Number"] = i + 1;
        obj["Status (stts_name)"] = "Pending";
        newData.push(obj);
      }
    }
    let reportName =
      data.propertyType !== "vehicle"
        ? data.address +
          ", " +
          data.city +
          ", " +
          data.state +
          ", " +
          data.postcode
        : data.VIN;
    exportExcel(newData, reportName + "-report");
  };

  const downloadPDF = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
  };

  const handlePageChange = (page) => fetchCocs(page);

  const handlePerPageChange = async (newPerPage, page) => {
    try {
      setPending(true);
      let response;
      if (user.role !== "admin") {
        response = await CocModel.get({
          userId: user._id,
          page,
          limit: newPerPage,
        });
      } else {
        response = await CocModel.get({ page, limit: newPerPage });
      }
      setCocs(response.data.data);
      setFilteredCocs(response.data.data);
      setPerPage(newPerPage);
    } catch (error) {
      console.log(error);
    } finally {
      setPending(false);
      // setTimeout(() => fetchCocs(page), 60 * 1000);
    }
  };

  // Debounce search input (location)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLocation(searchLocation);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchLocation]);

  const fetchCocs = async (page = 1) => {
    try {
      setPending(true);

      const params = {
        page,
        limit: perPage,
        location: debouncedLocation || undefined,
        status: searchStatus || undefined,
      };

      // If user isn't admin, include their ID
      if (user.role !== "admin") {
        params.userId = user._id;
      }

      const response = await CocModel.get(params);

      setCocs(response.data.data);
      setFilteredCocs(response.data.data);
      setTotalRows(response.data.pagination.total);
    } catch (error) {
      console.error(error);
    } finally {
      setPending(false);
    }
  };

  // Fetch on initial load
  useEffect(() => {
    fetchCocs();
  }, []);

  // Fetch when location or status changes
  useEffect(() => {
    fetchCocs(1); // Reset to page 1
  }, [debouncedLocation, searchStatus]);

  return (
    <>
      <HStack
        justifyContent="space-between"
        alignItems={{ base: "start", md: "center" }}
        flexDirection={{ base: "column", md: "row" }}
        mb={{ base: 0, md: "2" }}
      >
        <Heading fontSize={{ base: "xl", md: "3xl" }}>All Orders</Heading>
        <HStack
          gap="2"
          minW={{ base: "100%", md: "xl" }}
          flexDirection={{ base: "column", md: "row" }}
        >
          <CInput
            placeholder="Search Testing Location"
            value={searchLocation}
            onChange={(e) => setSearchLocation(e.target.value)}
          />
          <CSelect
            placeholder="Report Status"
            options={statusOptions}
            onChange={(opt) => setSearchStatus(opt ? opt.value : null)}
            isClearable={true}
          />
        </HStack>
      </HStack>
      <DataTable
        columns={columns}
        data={filteredCocs}
        progressPending={pending}
        keyField="_id"
        customStyles={customStyles}
        responsive
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerPageChange}
        onChangePage={handlePageChange}
        expandableRows
        expandableRowsHideExpander={windowSize > 1024}
        expandableRowsComponent={expandableData}
      />
    </>
  );
}
